import { DEFAULT_FONT } from './fonts';
import { DEFAULT_INK, type InkId } from './inks';
import { DISALLOWED, isSymbol, TEXT_PRESENTATION } from './symbols';

/**
 * A110Z — what the customer engraves on the temples. Pure data (no DOM), kept by the try-on for
 * the whole session: changing the model keeps the texts, the font and the colour; only the temples
 * drawn change (design handoff: « Gravure conservée sur {Modèle} »).
 *
 * Four slots: `il` inner left (the Maison Mata wordmark, locked), `ir` inner right (« Handcrafted
 * in Bali » by default, editable), `el` / `er` outer left / right (empty, optional). One font and
 * one ink for every text. Each editable slot also keeps the customer's size and position choices.
 *
 * A110Z2 (David, 6 octobre) : the size is a real letter height in mm (capital height), chosen per line
 * with a stepped slider — the same step gives the same height on every line and every model, and the
 * workshop receives it as is. Helvetica was the starting font; A201 (David, 11 octobre) : EB Garamond, 2,5 mm on every
 * line by default — the inside of the right temple included.
 */
export type SlotKey = 'il' | 'ir' | 'el' | 'er';
export type EditableSlot = 'ir' | 'el' | 'er';
export type TempleSide = 'l' | 'r';
export type TempleFace = 'int' | 'ext';

export const SLOTS: readonly SlotKey[] = ['il', 'ir', 'el', 'er'];
export const EDITABLE_SLOTS: readonly EditableSlot[] = ['ir', 'el', 'er'];
export const LOCKED_SLOT: SlotKey = 'il';

export const DEFAULT_INNER_RIGHT = 'Handcrafted in Bali';
/** The house origin moves next to the protected logo whenever the right inscription is replaced or cleared. */
export function originAfterLogo(state: EngraveState): string {
  return state.texts.ir.trim() === DEFAULT_INNER_RIGHT ? '' : DEFAULT_INNER_RIGHT;
}

/** Readable limit (counter « n / 20 »); typing stays possible up to `HARD_MAX`. */
export const MAX_CHARS = 20;
export const HARD_MAX = 30;

/** Letter heights (capital height, mm) of the size slider's steps. The workshop engraves cleanly from 2 to 3 mm. */
export const CAP_STEPS_MM = [1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 6] as const;
export const DEFAULT_CAP_MM = 2.5;
/** The Maison Mata wordmark on the inner left temple (capital height, mm). */
export const LOGO_CAP_MM = 2.5;
/**
 * A110Z3 (David : « je veux que les gens puissent placer, tourner la gravure où ils veulent sur la branche ») :
 * a text is either placed automatically (null: in the engraving zone, from the hinge side, reduced if longer than
 * the zone) or freely, by its centre and its tilt:
 *   x — distance from the hinge end along the temple (mm, to the text's centre),
 *   y — offset across the temple from the zone's middle line (mm, positive = lower on screen),
 *   rot — tilt from the temple's axis (degrees, positive = clockwise on screen).
 * The same millimetres apply on every model (a different temple may then be too short or too thin: the
 * preview says so).
 */
export interface FreePlace { x: number; y: number; rot: number }

/** Arrow nudges (mm) and tilt range / slider step (degrees). */
export const NUDGE_MM = 0.5;
export const ROT_MAX = 45;
export const ROT_STEP = 1;

export interface SlotStyle {
  /** Capital height in mm, one of `CAP_STEPS_MM`. */
  cap: number;
  /** Free placement, or null for the automatic one. */
  place: FreePlace | null;
}

export interface EngraveState {
  font: string;
  /** A201 (David : « une couleur par texte ») : the ink of each view, the logo's included. */
  inks: Record<SlotKey, InkId>;
  texts: Record<EditableSlot, string>;
  style: Record<EditableSlot, SlotStyle>;
  /** A201 : the Maison Mata logo can be tilted, made smaller or larger and moved — never removed. */
  logo: SlotStyle;
}

export const defaultStyle = (): SlotStyle => ({ cap: DEFAULT_CAP_MM, place: null });
export const defaultLogoStyle = (): SlotStyle => ({ cap: LOGO_CAP_MM, place: null });
export const defaultInks = (): Record<SlotKey, InkId> => ({ il: DEFAULT_INK, ir: DEFAULT_INK, el: DEFAULT_INK, er: DEFAULT_INK });

/** A201: size and placement of any view — the logo's for the inside left, the text's elsewhere. */
export function styleOf(state: EngraveState, slot: SlotKey): SlotStyle {
  return slot === LOCKED_SLOT ? state.logo : state.style[slot as EditableSlot];
}

/** A201: the ink of a view. */
export function inkOf(state: EngraveState, slot: SlotKey): InkId {
  return state.inks[slot];
}

/** Nearest slider step to a height (mm). */
export function capStep(mm: number): number {
  let best = 0;
  CAP_STEPS_MM.forEach((v, i) => { if (Math.abs(v - mm) < Math.abs(CAP_STEPS_MM[best]! - mm)) best = i; });
  return best;
}

export function defaultEngraveState(): EngraveState {
  return {
    font: DEFAULT_FONT,
    inks: defaultInks(),
    texts: { ir: DEFAULT_INNER_RIGHT, el: '', er: '' },
    style: { ir: defaultStyle(), el: defaultStyle(), er: defaultStyle() },
    logo: defaultLogoStyle(),
  };
}

export function slotSide(slot: SlotKey): TempleSide { return slot[1] as TempleSide; }
export function slotFace(slot: SlotKey): TempleFace { return slot[0] === 'i' ? 'int' : 'ext'; }
export function slotOf(side: TempleSide, face: TempleFace): SlotKey { return `${face === 'int' ? 'i' : 'e'}${side}` as SlotKey; }
export function isEditable(slot: SlotKey): slot is EditableSlot { return slot !== LOCKED_SLOT; }

/** Length as the customer counts it: the text presentation selectors do not count. */
export function textLength(text: string): number {
  return Array.from(text.replace(/︎/g, '')).length;
}

export interface Cleaned { text: string; stripped: boolean }

/**
 * Cleans typed text: disallowed characters (colour emoji outside our symbols, unsupported pictographs)
 * are removed, the text is cut at `HARD_MAX` characters, and a dangling selector is dropped.
 *
 * A110Z3: one of our symbols typed from the phone's emoji keyboard (🌴, ⛵ often followed by U+FE0F) is kept —
 * silently — and, like a symbol from the palette, always followed by exactly one U+FE0E (text presentation:
 * the try-on draws our engravable shape, never the colour emoji).
 */
export function cleanText(raw: string): Cleaned {
  const typed = raw.normalize('NFC').replace(/\uFE0F/g, '');
  const kept = typed.replace(DISALLOWED, '');
  const stripped = kept !== typed;
  const glyphs: string[] = [];
  for (const g of Array.from(kept)) {
    if (g === TEXT_PRESENTATION) continue;
    glyphs.push(isSymbol(g) ? g + TEXT_PRESENTATION : g);
  }
  // Cut to HARD_MAX counted characters (each symbol keeps its selector).
  return { text: glyphs.slice(0, HARD_MAX).join(''), stripped };
}

export function tooLong(text: string): boolean {
  return textLength(text) > MAX_CHARS;
}

/** Removes the last counted character (a symbol goes with its selector). */
export function deleteLast(text: string): string {
  return Array.from(text.replace(/︎$/, '')).slice(0, -1).join('');
}

/** The slot's text is the original one (« Handcrafted in Bali » inside right, nothing elsewhere). */
export function slotIsDefault(state: EngraveState, slot: EditableSlot): boolean {
  const text = state.texts[slot];
  return slot === 'ir' ? text === DEFAULT_INNER_RIGHT : text === '';
}

/**
 * Something differs from the house engraving: a changed or added text, another font or colour, or a
 * text resized or moved (each of these changes what the workshop engraves).
 */
export function isCustom(state: EngraveState): boolean {
  if (state.font !== DEFAULT_FONT || SLOTS.some(s => state.inks[s] !== DEFAULT_INK) || !logoIsDefault(state.logo)) return true;
  return EDITABLE_SLOTS.some(s => !slotIsDefault(state, s) || (state.texts[s] !== '' && !styleIsDefault(state.style[s])));
}

/** A201: the logo at its house size and place. */
export function logoIsDefault(style: SlotStyle): boolean {
  return Math.abs(style.cap - LOGO_CAP_MM) < 1e-6 && style.place === null;
}

/** Slots carrying a customer text (the emptied inner right counts: it is a change to transmit). */
export function customSlots(state: EngraveState): EditableSlot[] {
  return EDITABLE_SLOTS.filter(s => !slotIsDefault(state, s));
}

/** Number of texts actually engraved for the customer (non-empty custom texts). */
export function customTextCount(state: EngraveState): number {
  return customSlots(state).filter(s => state.texts[s] !== '').length;
}

export function styleIsDefault(style: SlotStyle): boolean {
  return Math.abs(style.cap - DEFAULT_CAP_MM) < 1e-6 && style.place === null;
}

/** A placement rounded to 0.1 mm / 0.5°, the tilt kept within ±ROT_MAX. */
export function roundPlace(p: FreePlace): FreePlace {
  const r = (v: number, k: number) => Math.round(v / k) * k;
  return { x: r(p.x, 0.1), y: r(p.y, 0.1), rot: Math.max(-ROT_MAX, Math.min(ROT_MAX, r(p.rot, 0.5))) };
}

/**
 * What leaves the try-on (WhatsApp, cart) once something is custom: font and ink, then every text the
 * workshop engraves — the inside right always (its text, or empty when the customer removed it), the
 * outside ones when written — each with its letter height (mm) and, when placed by hand, its placement.
 */
export interface EngravePayload {
  font: string;
  /** A201: the logo as set (its ink, size, and placement when moved). */
  logo: { ink: InkId; cap: number; place: FreePlace | null; suffix?: string };
  slots: Array<{ slot: EditableSlot; text: string; cap: number; place: FreePlace | null; ink: InkId }>;
}

export function engravePayload(state: EngraveState): EngravePayload | null {
  if (!isCustom(state)) return null;
  return fullPayload(state);
}

/** A201: the same, whether or not anything differs from the house engraving (the workshop sheet shows it all). */
export function fullPayload(state: EngraveState): EngravePayload {
  return {
    font: state.font,
    logo: { ...(originAfterLogo(state) ? { suffix: originAfterLogo(state) } : {}), ink: state.inks.il, cap: state.logo.cap, place: state.logo.place ? { ...state.logo.place } : null },
    slots: EDITABLE_SLOTS.filter(slot => slot === 'ir' || state.texts[slot] !== '').map(slot => {
      const style = state.style[slot];
      return { slot, text: state.texts[slot], cap: style.cap, place: style.place ? { ...style.place } : null, ink: state.inks[slot] };
    }),
  };
}

function copyStyle(s: SlotStyle): SlotStyle {
  return { cap: s.cap, place: s.place ? { ...s.place } : null };
}

/** Deep copy (the UI mutates its own copy and hands back a new state). */
export function cloneState(state: EngraveState): EngraveState {
  return {
    font: state.font,
    inks: { ...state.inks },
    texts: { ...state.texts },
    style: { ir: copyStyle(state.style.ir), el: copyStyle(state.style.el), er: copyStyle(state.style.er) },
    logo: copyStyle(state.logo),
  };
}

/** A201: two states describe the same engraving (Undo skips no-op steps). */
export function sameState(a: EngraveState, b: EngraveState): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
